<template>
    <Dialog
        :visible="issued !== null"
        modal
        header="Copy the client secret now"
        :closable="false"
        :dismissable-mask="false"
        :close-on-escape="false"
        :draggable="false"
        class="issued-secret"
        :style="{ width: '34rem', maxWidth: '92vw' }"
    >
        <Message severity="warn" :closable="false" class="issued-secret__warning">
            <strong>This secret is shown once.</strong> It is not saved anywhere and it cannot be recovered. Copy it now
            and give it to the program; once this dialog closes, nothing can show it again.
        </Message>

        <p v-if="issued && issued.expiring && issued.expiring.length > 0" class="issued-secret__expiring" data-expiring>
            The credential this one replaces keeps working until {{ expiringUntil }}, then it is revoked. Switch the program
            to the new one before then.
        </p>

        <dl v-if="issued" class="issued-secret__fields">
            <div class="issued-secret__field">
                <dt>Machine ID</dt>
                <dd><code>{{ issued.machineId }}</code></dd>
            </div>
            <div class="issued-secret__field">
                <dt>Client ID</dt>
                <dd>
                    <code data-secret="client-id">{{ issued.clientId }}</code>
                    <Button
                        :label="copiedLabel('clientId')"
                        icon="pi pi-copy"
                        severity="secondary"
                        variant="outlined"
                        size="small"
                        data-copy="client-id"
                        @click="copy('clientId')"
                    />
                </dd>
            </div>
            <div class="issued-secret__field">
                <dt>Client secret</dt>
                <dd>
                    <code data-secret="client-secret" class="issued-secret__secret">{{ issued.clientSecret }}</code>
                    <Button
                        :label="copiedLabel('clientSecret')"
                        icon="pi pi-copy"
                        severity="secondary"
                        variant="outlined"
                        size="small"
                        data-copy="client-secret"
                        @click="copy('clientSecret')"
                    />
                </dd>
            </div>
        </dl>
        <p v-if="copyFailed" class="issued-secret__copy-failed" role="alert">
            The clipboard refused the copy. Select the value and copy it by hand before closing.
        </p>

        <template #footer>
            <Button label="I have copied it" icon="pi pi-check" data-acknowledge @click="emit('acknowledged')" />
        </template>
    </Dialog>
</template>

<script setup>
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import Message from 'primevue/message'
import { computed, ref, watch } from 'vue'

defineOptions({ name: 'IssuedSecretDialog' })

// The one screen a client secret is ever on. Making a Machine or rotating its credential
// answers the secret once; the identity server keeps it nowhere and cannot recover it. So the
// dialog says that in plain words, offers a copy for the id and for the secret, and does not
// close on a stray click, Escape, or an x it does not have: the only way out is "I have copied
// it". The owner clears `issued` on that, and nothing in the app holds the secret afterwards.
const props = defineProps({
    // `{ machineId, clientId, clientSecret }` to show, or null for closed.
    issued: { type: Object, default: null }
})

const emit = defineEmits(['acknowledged'])

const copied = ref(null)
const copyFailed = ref(false)

// When the credential a rotation replaced stops working: the earliest expiry among them, read
// only when the answer names one (a brand-new Machine replaces nothing).
const expiringUntil = computed(function () {
    const soonest = [...props.issued.expiring].map((credential) => credential.expiresAt).sort()[0]
    return new Date(soonest).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
})

watch(() => props.issued, function () {
    copied.value = null
    copyFailed.value = false
})

function copiedLabel(field) {
    if (copied.value === field) {
        return 'Copied'
    }
    return 'Copy'
}

async function copy(field) {
    copyFailed.value = false
    try {
        await navigator.clipboard.writeText(props.issued[field])
        copied.value = field
    } catch {
        copyFailed.value = true
    }
}
</script>

<style scoped>
.issued-secret__warning {
    margin: 0 0 16px;
}

.issued-secret__fields {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 0;
}

.issued-secret__field dt {
    font-size: 12px;
    font-weight: 600;
    color: var(--p-text-muted-color);
    margin: 0 0 4px;
}

.issued-secret__field dd {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
}

.issued-secret__field code {
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
    font-family: var(--p-font-mono);
    font-size: 13px;
    padding: 6px 8px;
    border: 1px solid var(--p-content-border-color);
    border-radius: 6px;
    background: var(--surface-ground);
}

.issued-secret__secret {
    letter-spacing: 0.04em;
}

.issued-secret__copy-failed {
    margin: 12px 0 0;
    font-size: 13px;
    color: var(--p-status-danger);
}
</style>
